/**
 * OrbitalNodeAtom - A data-driven orbital node component
 *
 * Renders a sphere as an orbital node with:
 * - Status-colored ring with gradient (behind chrome overlay)
 * - Chrome orbital ring overlay (transparent PNG)
 * - Avatar or initials in center
 * - Sphere name label
 * - Dynamic sizing based on impact score
 * - Orbiting satellites with status colors
 */

import React, { useMemo } from 'react';
import type { Sphere, SatelliteSphere } from 'types/global';
import { getStatusColor, getStatusFromSphere } from '../../../utils/statusColors';
import type { ExtendedStatusValue } from '../../../utils/statusColors';
import { getOrbitalImage, getOrbitalStatusColor, ORBITAL_RING_DIMENSIONS } from '../../../utils/orbitalImages';
import './OrbitalNodeAtom.scss';

/** Size calculation constants */
const NODE_BASE_SIZE = 50;
const NODE_MAX_GROWTH = 30;

export interface OrbitalNodeAtomProps {
  /** Sphere data to display */
  sphere: Sphere;
  /** Override calculated size (diameter in pixels) */
  size?: number;
  /** Impact score (0-100) for dynamic sizing - ignored if size is provided */
  impactScore?: number;
  /** Show sphere name label below node */
  showLabel?: boolean;
  /** Custom label text (overrides sphere.properties.name) */
  label?: string;
  /** Click handler */
  onClick?: () => void;
  /** Additional CSS class */
  className?: string;
  /** Avatar image URL */
  avatarUrl?: string;
  /** Unique ID for SVG definitions (required when rendering multiple nodes) */
  nodeId?: string;
  /** Satellite spheres to display orbiting around the node */
  satellites?: SatelliteSphere[];
  /** Enable satellite orbit animation (default: true) */
  animateSatellites?: boolean;
}

/**
 * Calculate node size based on impact score
 */
function calculateNodeSize(impactScore: number): number {
  const normalizedImpact = Math.max(0, Math.min(100, impactScore)) / 100;
  return NODE_BASE_SIZE + normalizedImpact * NODE_MAX_GROWTH;
}

/**
 * Get initials from sphere name
 */
function getInitials(name: string): string {
  if (!name) return '?';
  const words = name.trim().split(/\s+/);
  if (words.length === 1) {
    return words[0].substring(0, 2).toUpperCase();
  }
  return (words[0][0] + words[1][0]).toUpperCase();
}

const OrbitalNodeAtom: React.FC<OrbitalNodeAtomProps> = ({
  sphere,
  size: sizeProp,
  impactScore = 50,
  showLabel = true,
  label,
  onClick,
  className = '',
  avatarUrl,
  nodeId = 'default',
  satellites = [],
  animateSatellites = true,
}) => {
  // Calculate size
  const size = sizeProp ?? calculateNodeSize(impactScore);

  // Get status and colors
  const status = getStatusFromSphere(sphere);
  const statusColor = getOrbitalStatusColor(status);
  const orbitalImage = getOrbitalImage();

  // Get display info
  const sphereName = label || sphere?.properties?.name || 'Sphere';
  const initials = getInitials(sphereName);

  // Calculate dimensions
  const nodeRadius = size / 2;
  const innerRadius = nodeRadius * 0.52; // Inner content area
  const avatarRadius = nodeRadius * 0.48; // Avatar size
  const satelliteOrbitRadius = nodeRadius * 1.4; // Just outside the orbital ring
  const viewBoxSize = size * 1.6; // Extra space for satellites
  const center = viewBoxSize / 2;

  // Status ring dimensions
  const ringRadius = nodeRadius * ORBITAL_RING_DIMENSIONS.ringCenterRadius;
  const strokeWidth = size * ORBITAL_RING_DIMENSIONS.strokeWidth;

  // Unique IDs for SVG definitions
  const avatarClipId = `avatar-clip-${nodeId}`;
  const gradientId = `status-gradient-${nodeId}`;

  // Memoize whether we have an avatar
  const hasAvatar = useMemo(() => !!avatarUrl, [avatarUrl]);

  // Calculate satellite positions and colors
  const satelliteData = useMemo(() => {
    const maxSatellites = Math.min(satellites.length, 5);
    const baseSizes = [4, 3, 4, 3, 3];
    const sizeScale = size / 50;
    return satellites.slice(0, maxSatellites).map((sat, index) => {
      const angle = (index * 2 * Math.PI) / maxSatellites - Math.PI / 2;
      const color = getStatusColor(sat.status as ExtendedStatusValue);
      return {
        id: sat.id,
        name: sat.name,
        angle,
        cx: center + satelliteOrbitRadius * Math.cos(angle),
        cy: center + satelliteOrbitRadius * Math.sin(angle),
        r: baseSizes[index] * sizeScale,
        color,
      };
    });
  }, [satellites, center, satelliteOrbitRadius, size]);

  return (
    <button
      type="button"
      className={`orbital-node ${className}`}
      style={{ width: viewBoxSize, height: viewBoxSize }}
      onClick={onClick}
    >
      <svg
        viewBox={`0 0 ${viewBoxSize} ${viewBoxSize}`}
        className="orbital-node__svg"
        aria-label={`${sphereName} - ${status}`}
      >
        <defs>
          {/* Gradient for status ring */}
          <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={statusColor} stopOpacity={0.9} />
            <stop offset="50%" stopColor={statusColor} stopOpacity={1} />
            <stop offset="100%" stopColor={statusColor} stopOpacity={0.9} />
          </linearGradient>
          {/* Clip path for avatar */}
          <clipPath id={avatarClipId}>
            <circle cx={center} cy={center} r={avatarRadius} />
          </clipPath>
        </defs>

        {/* Layer 1: Status color ring (behind chrome overlay) */}
        <circle
          cx={center}
          cy={center}
          r={ringRadius}
          fill="none"
          stroke={`url(#${gradientId})`}
          strokeWidth={strokeWidth}
          className={`orbital-node__status-ring orbital-node__status-ring--${status}`}
          data-status={status}
        />

        {/* Layer 2: Chrome overlay image (transparent PNG) */}
        <image
          href={orbitalImage}
          x={center - nodeRadius}
          y={center - nodeRadius}
          width={size}
          height={size}
          preserveAspectRatio="xMidYMid meet"
          className="orbital-node__chrome-image"
        />

        {/* Layer 3: Inner circle (content background) */}
        <circle
          cx={center}
          cy={center}
          r={innerRadius}
          fill="#1a2744"
          className="orbital-node__inner-bg"
        />

        {/* Layer 4: Avatar or Initials */}
        {hasAvatar ? (
          <image
            href={avatarUrl}
            x={center - avatarRadius}
            y={center - avatarRadius}
            width={avatarRadius * 2}
            height={avatarRadius * 2}
            clipPath={`url(#${avatarClipId})`}
            preserveAspectRatio="xMidYMid slice"
            className="orbital-node__avatar"
          />
        ) : (
          <text
            x={center}
            y={center}
            textAnchor="middle"
            dominantBaseline="central"
            fill="#ffffff"
            fontSize={nodeRadius * 0.45}
            fontWeight="600"
            fontFamily="var(--font-family, sans-serif)"
            className="orbital-node__initials"
          >
            {initials}
          </text>
        )}

        {/* Layer 5: Satellites */}
        {satelliteData.length > 0 && (
          <g className={`orbital-node__satellites ${animateSatellites ? 'orbital-node__satellites--animated' : ''}`}
             style={{ transformOrigin: `${center}px ${center}px` }}>
            {/* Subtle orbit path for satellites */}
            <circle
              cx={center}
              cy={center}
              r={satelliteOrbitRadius}
              fill="none"
              stroke="#7EDDF1"
              strokeWidth="1"
              opacity="0.15"
              className="orbital-node__satellite-orbit"
            />
            {/* Satellite circles */}
            {satelliteData.map((sat) => (
              <circle
                key={sat.id}
                cx={sat.cx}
                cy={sat.cy}
                r={sat.r}
                fill={sat.color}
                className="orbital-node__satellite"
                style={{
                  filter: 'drop-shadow(0 0 2px rgba(0, 0, 0, 0.3))',
                }}
              >
                <title>{sat.name}</title>
              </circle>
            ))}
          </g>
        )}
      </svg>

      {/* Label below the node */}
      {showLabel && (
        <div className="orbital-node__label" title={sphereName}>
          <span className="orbital-node__label-text">{sphereName}</span>
        </div>
      )}
    </button>
  );
};

export default OrbitalNodeAtom;
